import React, { useState, useEffect, useCallback, useRef } from "react";
import LoadingImage from "./assets/Loading.gif";
import { IoCarSportOutline } from "react-icons/io5";
import HTTP_BASE_URL from "../constants/HttpConstants";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const toastrOptions = {
  position: "top-center",
  autoClose: 1000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "colored",
};

const validateCoordinates = (lat, lng) => {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    !isNaN(lat) &&
    !isNaN(lng)
  );
};

const defaultCenter = { lat: 13.060644638899205, lng: 80.27414512422938 };

const getLatLngFromString = (latLngStr) => {
  if (!latLngStr) return defaultCenter;
  const [lat, lng] = latLngStr.split(",").map(parseFloat);
  return validateCoordinates(lat, lng) ? { lat, lng } : defaultCenter;
};

const CarDetails = ({ close, id }) => {
  const customertoken = localStorage.getItem("customertoken");
  const navigate = useNavigate();
  const [carData, setCarData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // eslint-disable-next-line
  const [closePopup, setClosePopup] = useState();
  const [suggestions, setSuggestions] = useState({
    carmake: [],
    carmodel: [],
  });
  const [inputValues, setInputValues] = useState({});
  const pickUpTimeRef = useRef();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey:
      "AIzaSyAX84ZKpYIp9O7prq3tsjFn_G4eeEdb9KY&libraries=places&loading=async",
  });
  const [center, setCenter] = useState(defaultCenter);
  const [map, setMap] = useState(null);
  // const [markerPickup, setMarkerPickup] = useState({ lat: 0, lng: 0 });
  // const [markerDelivery, setMarkerDelivery] = useState({ lat: 0, lng: 0 });
  const [activeMarker, setActiveMarker] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [clear, setClear] = useState(false);

  const carModel = useCallback(() => {
    fetch(`${HTTP_BASE_URL}getCarModel`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "access-token": "508b1430-68f0-4b2f-9003-3c2a0eb2a54a",
        customertoken: customertoken,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.code === 0) {
          const carDataArray = [];
          Object.keys(data.data).forEach((make) => {
            Object.keys(data.data[make]).forEach((model) => {
              Object.keys(data.data[make][model]).forEach((type) => {
                const colors = data.data[make][model][type];
                colors.forEach((color) => {
                  carDataArray.push({ make, model, type, color });
                });
              });
            });
          });
          setCarData(carDataArray);
        } else {
          navigate("/");
        }
      })
      .catch((error) => console.error("Error fetching car data:", error));
  }, [customertoken, navigate]);

  const tokenExpiry = useCallback(() => {
    if (!customertoken) {
      // Step 1: Clear storage
      localStorage.clear(); // or sessionStorage.clear();
      // Step 2: Redirect to login or home page
      navigate("/", { replace: true });
      // Step 3: Prevent going back to the previous page
      window.history.pushState(null, "", window.location.href);
      window.onpopstate = () => {
        window.history.pushState(null, "", window.location.href);
      };
    }
  }, [customertoken, navigate]);

  useEffect(() => {
    carModel();
    tokenExpiry();
  }, [carModel, tokenExpiry]);

  const fetchCarMakes = (query) => {
    fetch(HTTP_BASE_URL + "getCarModel", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "access-token": "508b1430-68f0-4b2f-9003-3c2a0eb2a54a",
        customertoken: customertoken,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const carDataArray = [];
        Object.keys(data.data).forEach((make) => {
          Object.keys(data.data[make]).forEach((model) => {
            Object.keys(data.data[make][model]).forEach((type) => {
              const colors = data.data[make][model][type];
              colors.forEach((color) => {
                carDataArray.push({ make, model, type, color });
              });
            });
          });
        });

        setCarData(carDataArray);
        const filteredMakes = [
          ...new Set(
            carDataArray
              .filter((item) =>
                item.make.toLowerCase().includes(query.toLowerCase())
              )
              .map((item) => item.make)
          ),
        ];
        setSuggestions((prev) => ({ ...prev, carmake: filteredMakes }));
      })
      .catch((error) => console.error("Error fetching car makes:", error));
  };

  const handleModelKeyPress = (event) => {
    const query = event.target.value;
    setInputValues((prev) => ({ ...prev, carmodel: query })); // Update input value dynamically
    fetchCarModels(inputValues.carmake, query); // Fetch matching models
  };

  const fetchCarModels = (selectedMake, query) => {
    if (!selectedMake) return; // Ensure parent selections are made
    const filteredModels = [
      ...new Set(
        carData
          .filter(
            (item) =>
              item.make === selectedMake &&
              // item.type === selectedType &&
              item.model.toLowerCase().includes(query.toLowerCase())
          )
          .map((item) => item.model)
      ),
    ];
    setSuggestions((prev) => ({
      ...prev,
      carmodel: filteredModels.length > 0 ? filteredModels : false, // Handle empty results
    }));
  };

  const safeSetCenter = (lat, lng) => {
    if (validateCoordinates(lat, lng)) {
      setCenter({ lat, lng });
    } else {
      console.error("Invalid center coordinates:", { lat, lng });
    }
  };

  const goToCurrentPickupLocation = () => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      alert("Geolocation is not supported by this browser.");
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const currentLat = position.coords.latitude;
          const currentLng = position.coords.longitude;

          setInputValues((prevState) => {
            const updatedCenter = !prevState.pickuplocation_latlon
              ? { lat: currentLat, lng: currentLng }
              : getLatLngFromString(prevState.pickuplocation_latlon);

            safeSetCenter(updatedCenter.lat, updatedCenter.lng);

            return {
              ...prevState,
              pickuplocation_latlon: prevState.pickuplocation_latlon || "",
              // `${currentLat},${currentLng}`,   // current location select
            };
          });
        },
        (error) => {
          console.error("Error getting current location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const goToCurrentDeliveryLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const currentLat = position.coords.latitude;
          const currentLng = position.coords.longitude;

          setInputValues((prevState) => {
            const updatedCenter = !prevState.deliverylocation_latlon
              ? { lat: currentLat, lng: currentLng }
              : getLatLngFromString(prevState.deliverylocation_latlon);

            safeSetCenter(updatedCenter.lat, updatedCenter.lng);

            return {
              ...prevState,
              deliverylocation_latlon: prevState.deliverylocation_latlon || "",
              // `${currentLat},${currentLng}`,    // current location select
            };
          });
        },
        (error) => {
          console.error("Error getting current location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const onMapClick = useCallback(
    (event) => {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      if (!validateCoordinates(newPosition.lat, newPosition.lng)) {
        console.error("Invalid click position:", newPosition);
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPosition }, (results, status) => {
        if (status === "OK" && results[0]) {
          const formattedAddress = results[0].formatted_address;
          if (activeMarker === "pickup") {
            setInputValues((prevState) => ({
              ...prevState,
              pickuplocation_latlon: `${newPosition.lat},${newPosition.lng}`,
              pickuplocation: formattedAddress,
            }));
          } else {
            setInputValues((prevState) => ({
              ...prevState,
              deliverylocation_latlon: `${newPosition.lat},${newPosition.lng}`,
              deliverylocation: formattedAddress,
            }));
          }
          if (map) {
            safeSetCenter(newPosition.lat, newPosition.lng);
          }
        } else {
          console.error("Geocoder failed or no results found.");
        }
      });
    },
    [activeMarker, map]
  );

  const onMarkerDragEnd = useCallback(
    (event, markerType) => {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      if (!validateCoordinates(newPosition.lat, newPosition.lng)) {
        console.error("Invalid drag position:", newPosition);
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPosition }, (results, status) => {
        if (status === "OK" && results[0]) {
          const formattedAddress = results[0].formatted_address;
          if (markerType === "pickup") {
            setInputValues((prevState) => ({
              ...prevState,
              pickuplocation_latlon: `${newPosition.lat},${newPosition.lng}`,
              pickuplocation: formattedAddress,
            }));
          } else if (markerType === "delivery") {
            setInputValues((prevState) => ({
              ...prevState,
              deliverylocation_latlon: `${newPosition.lat},${newPosition.lng}`,
              deliverylocation: formattedAddress,
            }));
          }

          if (map) {
            safeSetCenter(newPosition.lat, newPosition.lng);
          }
        } else {
          console.error(
            "Geocoder failed or no results found for dragged marker."
          );
        }
      });
    },
    [map]
  );

  useEffect(() => {
    if (
      inputValues?.pickuplocation_latlon?.length > 0 ||
      inputValues?.deliverylocation_latlon?.length > 0
    ) {
      setClear(true);
    }
  }, [
    inputValues?.pickuplocation_latlon,
    inputValues?.deliverylocation_latlon,
  ]);

  const closeModal = () => {
    setModalVisible(false);
  };

  const setLocation = () => {
    if (
      inputValues.pickuplocation_latlon?.length > 0 ||
      inputValues.deliverylocation_latlon?.length > 0
    ) {
      closeModal();
    } else {
      // alert('Please Select Location');
      toast.warning("Please select map locations", {
        toastrOptions,
      });
    }
  };

  const clearLocations = () => {
    setInputValues((prevState) => ({
      ...prevState,
      pickuplocation_latlon: "",
      deliverylocation_latlon: "",
      pickuplocation: "",
      deliverylocation: "",
    }));
    // setMarkerPickup('');
    // setMarkerDelivery('')
    setClear(false);
    if (map) {
      map.setZoom(15);
      // map.panTo({ lat: 13.060644638899205, lng: 80.27414512422938 });
    }
    // closeModal();
  };

  let flatpickrInstance = null;
  const handleDateTimeClick = () => {
    if (!flatpickrInstance) {
      flatpickrInstance = flatpickr(pickUpTimeRef.current, {
        enableTime: true,
        dateFormat: "Y-m-d H:i:S",
        clickOpens: true,
        disableMobile: "true",
        onChange: function (selectedDates, dateStr) {
          setInputValues((prev) => ({
            ...prev,
            pickuptime: dateStr,
          }));
        },
        onReady: function (selectedDates, dateStr, instance) {
          const buttonContainer = document.createElement("div");
          buttonContainer.classList.add("flatpickr-button-container");

          const okButton = document.createElement("button");
          okButton.textContent = "OK";
          okButton.classList.add("flatpickr-ok-button");

          const todayButton = document.createElement("button");
          todayButton.textContent = "Today";
          todayButton.classList.add("flatpickr-today-button");

          // Append buttons to the container and Flatpickr calendar
          buttonContainer.appendChild(todayButton);
          buttonContainer.appendChild(okButton);
          instance.calendarContainer.appendChild(buttonContainer);

          // Today button: Set to current date and trigger onChange
          todayButton.addEventListener("click", () => {
            instance.setDate(new Date(), true); // Pass `true` to trigger onChange
            instance.close();
          });

          // OK button: Close calendar if value exists
          okButton.addEventListener("click", () => {
            if (!pickUpTimeRef.current.value) {
              // alert("Please select a date and time.");
              toast.warning("Please select a date and time.", {
                toastrOptions,
              });
            } else {
              instance.close();
            }
          });
        },
      });
    }
  };

  const handleOnChange = (e) => {
    const { name, value, checked, type } = e.target;
    setInputValues({
      ...inputValues,
      [name]: type === "checkbox" ? checked : value,
    });
    if (name === "carmake") {
      fetchCarMakes(value);
    }
    const error = validateField(name, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "email":
        if (!value) {
          error = "";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = "Invalid email address.";
        }
        break;
      case "carmake":
        if (!value) {
          error = "This field is required.";
        } else if (value.length < 2) {
          error = "Please enter at least 2 characters.";
          // } else if (!/^[a-zA-Z\s\.\-]+$/.test(value)) {
          //     error = "Please enter letters, spaces, hyphens, and periods only.";
          // }
        } else if (!/^[a-zA-Z\s.-]+$/.test(value)) {
          error = "Only letters, spaces, hyphens, and periods are allowed.";
        }
        break;
      case "carmodel":
        if (!value) {
          error = "This field is required.";
        }
        break;
      case "pickuptime":
        if (!value) {
          error = "This field is required.";
        }
        break;
      case "pickuplocation_latlon":
      case "deliverylocation_latlon":
        if (!value) {
          error = "This field is required.";
        } else if (value === "null") {
          error = "";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleSuggestionClick = (field, value) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
    setSuggestions((prev) => ({ ...prev, [field]: [] }));
  };

  const CarDetailSubmit = async (event) => {
    event.preventDefault();
    const newErrors = {};
    // Validate fields and collect errors
    Object.keys(inputValues).forEach((fieldName) => {
      const error = validateField(fieldName, inputValues[fieldName]);
      if (error) newErrors[fieldName] = error;
    });
    if (Object.keys(newErrors).length > 0) {
      // alert('Please fill in all fields correctly');
      toast.warning("Please ensure all fields are filled in correctly.", {
        toastrOptions,
      });
      // console.log(newErrors)
      return;
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true); // Show loading spinner

      // Handle payment type-specific logic
      let updatedValues = { ...inputValues };
      // if (inputValues.payment_type === "1") {
      //     updatedValues.company_name = "null";
      //     updatedValues.bill_status = "0";
      // }
      // Create FormData for submission

      const formData = new FormData();
      formData.append("bookingid", id);

      Object.keys(updatedValues).forEach((key) => {
        formData.append(key, updatedValues[key]);
      });
      // console.log('formData', formData)
      try {
        // Simulate delay for spinner demo
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Submit the form data via API
        const response = await fetch(HTTP_BASE_URL + "saveBookingdetails", {
          method: "post",
          headers: {
            "access-token": "508b1430-68f0-4b2f-9003-3c2a0eb2a54a",
            customertoken: customertoken,
          },
          body: formData,
        });

        const result = await response.json();

        // Handle API response
        if (result.code === 0) {
          //  console.log("Form submitted successfully:", result);
          setIsSubmitted(true);
          toast.success("Thank you for your request!", { toastrOptions });
          setTimeout(function () {
            window.location.reload();
            setClosePopup(close);
          }, 1500);
        } else if (result.code === 1) {
          toast.error("Please complete all required fields correctly", {
            toastrOptions,
          });
        }
      } catch (error) {
        console.error("Submission failed:", error);
        // alert("An error occurred. Please try again.");
      } finally {
        setIsSubmitting(false); // Hide loading spinner
      }
    }
  };

  if (loadError) {
    return <p className="pageloading">Error loading maps</p>;
  }
  if (!isLoaded) {
    return <p className="pageloading">Loading...</p>;
  }

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      {modalVisible && (
        <div className="mapModal">
          <div className="mapModal-content">
            <div className="mapModal-header">
              <button
                type="button"
                className="btn-close map-close"
                onClick={closeModal}
              >
                <AiOutlineCloseCircle />
              </button>
            </div>
            <div className="mapModal-body">
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: "400px",
                }}
                zoom={15}
                center={center}
                onClick={onMapClick}
                onLoad={(map) => setMap(map)}
              >
                {inputValues.pickuplocation_latlon && (
                  <Marker
                    // position={markerPickup}
                    position={{
                      lat: parseFloat(
                        inputValues.pickuplocation_latlon.split(",")[0]
                      ),
                      lng: parseFloat(
                        inputValues.pickuplocation_latlon.split(",")[1]
                      ),
                    }}
                    draggable={true}
                    // onClick={() => setActiveMarker('pickup')}
                    onDragEnd={(event) => onMarkerDragEnd(event, "pickup")}
                    label="P"
                  />
                )}
                {inputValues.deliverylocation_latlon && (
                  <Marker
                    // position={markerDelivery}
                    position={{
                      lat: parseFloat(
                        inputValues.deliverylocation_latlon.split(",")[0]
                      ),
                      lng: parseFloat(
                        inputValues.deliverylocation_latlon.split(",")[1]
                      ),
                    }}
                    draggable={true}
                    // onClick={() => setActiveMarker('delivery')}
                    onDragEnd={(event) => onMarkerDragEnd(event, "delivery")}
                    label="D"
                  />
                )}
              </GoogleMap>
            </div>
            <div className="mapModal-footer">
              {clear && (
                <button type="button" id="clear_btn" onClick={clearLocations}>
                  Clear
                </button>
              )}
              <button
                type="button"
                className="btn map-submit btn-danger"
                onClick={setLocation}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fromModel">
        <div className="billingModal-content">
          <div className="div">
            <div className="gstModel-header">
              <p className="addGst">
                <IoCarSportOutline /> Get Quotation
              </p>
              <div className="btn-close" onClick={() => close(false)}>
                <AiOutlineCloseCircle />
              </div>
            </div>
            <div className="modal-body">
              <form onSubmit={CarDetailSubmit} autoComplete="off">
                <input
                  type="hidden"
                  name="pickuplocation"
                  value={inputValues?.pickuplocation || ""}
                />
                <input
                  type="hidden"
                  name="deliverylocation"
                  value={inputValues?.deliverylocation || ""}
                />
                <div className="row p-0 m-0">
                  <div className="col form-col col-md-6 col-12">
                    <label htmlFor="email" className="form-label">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      disabled={isSubmitting || isSubmitted}
                      value={
                        inputValues
                          ? inputValues?.email === "null" || !inputValues?.email
                            ? ""
                            : inputValues?.email
                          : ""
                      }
                      id="email"
                      onChange={handleOnChange}
                      className="form-control"
                      placeholder="Enter@youremail.com"
                    />
                    {errors.email && (
                      <p className="text-para red">{errors.email}</p>
                    )}
                  </div>

                  <div className="col form-col col-md-6 col-12">
                    <label htmlFor="carmake" className="form-label">
                      Car Make <span className="req-span">*</span>
                    </label>
                    <input
                      type="text"
                      id="carmake"
                      name="carmake"
                      disabled={isSubmitting || isSubmitted}
                      value={
                        inputValues
                          ? inputValues?.carmake === "null" ||
                            !inputValues?.carmake
                            ? ""
                            : inputValues?.carmake
                          : ""
                      }
                      onChange={handleOnChange}
                      className="form-control"
                      placeholder="Eg: Maruti Suzuki"
                    />
                    {errors.carmake && (
                      <p className="text-para red">{errors.carmake}</p>
                    )}
                    {suggestions.carmake.length > 0 && (
                      <ul>
                        {suggestions.carmake.map((make) => (
                          <li
                            key={make}
                            onClick={() =>
                              handleSuggestionClick("carmake", make)
                            }
                          >
                            {make}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="col form-col col-md-6 col-12">
                    <label htmlFor="carmodel" className="form-label">
                      Car Model <span className="req-span">*</span>
                    </label>
                    <input
                      type="text"
                      id="carmodel"
                      name="carmodel"
                      disabled={isSubmitting || isSubmitted}
                      value={
                        inputValues
                          ? inputValues?.carmodel === "null" ||
                            !inputValues?.carmodel
                            ? ""
                            : inputValues?.carmodel
                          : ""
                      }
                      onKeyDown={handleModelKeyPress}
                      onChange={handleOnChange}
                      className="form-control"
                      placeholder="Eg: Ertiga"
                    />
                    {errors.carmodel && (
                      <p className="red text-para">{errors.carmodel}</p>
                    )}

                    {suggestions.carmodel.length > 0 && (
                      <ul>
                        {suggestions.carmodel.map((model) => (
                          <li
                            key={model}
                            onClick={() =>
                              handleSuggestionClick("carmodel", model)
                            }
                          >
                            {model}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="col form-col col-md-6 col-12">
                    <label htmlFor="pickupLocationInput" className="form-label">
                      Pickup Map Location <span className="req-span">*</span>
                    </label>
                    <input
                      type="text"
                      name="pickuplocation_latlon"
                      id="pickupLocationInput"
                      placeholder="Select in Google map"
                      className="form-control map-input-img"
                      // value={inputValues?.pickuplocation_latlon || ''}
                      value={
                        inputValues?.pickuplocation_latlon === "null" ||
                        !inputValues?.pickuplocation_latlon
                          ? ""
                          : inputValues?.pickuplocation_latlon
                      }
                      readOnly
                      disabled={isSubmitting || isSubmitted}
                      onClick={() => {
                        setModalVisible(true);
                        setActiveMarker("pickup");
                        goToCurrentPickupLocation();
                      }}
                      onChange={handleOnChange}
                    />
                    {errors.pickuplocation_latlon && (
                      <p className="red text-para">
                        {errors.pickuplocation_latlon}
                      </p>
                    )}
                  </div>
                  <div className="col form-col col-md-6 col-12">
                    <label
                      htmlFor="deliveryLocationInput"
                      className="form-label"
                    >
                      Delivery Map Location <span className="req-span">*</span>
                    </label>
                    <input
                      type="text"
                      name="deliverylocation_latlon"
                      id="deliveryLocationInput"
                      placeholder="Select in Google map"
                      className="form-control map-input-img"
                      // value={inputValues?.deliverylocation_latlon || ''}
                      value={
                        inputValues?.deliverylocation_latlon === "null" ||
                        !inputValues?.deliverylocation_latlon
                          ? ""
                          : inputValues?.deliverylocation_latlon
                      }
                      disabled={isSubmitting || isSubmitted}
                      readOnly
                      onClick={() => {
                        setModalVisible(true);
                        setActiveMarker("delivery");
                        goToCurrentDeliveryLocation();
                      }}
                      onChange={handleOnChange}
                    />
                    {errors.deliverylocation_latlon && (
                      <p className="red text-para">
                        {errors.deliverylocation_latlon}
                      </p>
                    )}
                  </div>

                  <div className="col form-col col-md-6 col-12">
                    <label htmlFor="pickuptime" className="form-label">
                      Pickup Date & Time <span className="req-span">*</span>
                    </label>
                    <input
                      type="text"
                      id="pickuptime"
                      name="pickuptime"
                      disabled={isSubmitting || isSubmitted}
                      ref={pickUpTimeRef}
                      onClick={handleDateTimeClick}
                      value={
                        inputValues?.pickuptime === "null" ||
                        !inputValues?.pickuptime
                          ? ""
                          : inputValues?.pickuptime
                      }
                      onChange={handleOnChange}
                      className="form-control input-calendar-img"
                    />
                  </div>
                </div>
                <div className="modal-footer pt-lg-2rm pt-4 text-center">
                  <button
                    type="submit"
                    className="submit-btn mb-0"
                    disabled={isSubmitting || isSubmitted}
                  >
                    {isSubmitting
                      ? "Submitting..."
                      : isSubmitted
                      ? "Submitted"
                      : "Submit"}
                    <span className="sub-loading">
                      {isSubmitting && (
                        <img
                          src={LoadingImage}
                          alt="Progress indicator"
                          width={25}
                          height={25}
                          loading="eager"
                        />
                      )}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CarDetails;
